import { useNavigate } from 'react-router-dom';
import { useState, useContext } from 'react';
import { AttachFileButton } from 'atoms/AttachFileButton';
import { handleFileDownload } from './NotificationArea';
import { AppContext } from 'templates/AppContextProvider';
import MessageModal from 'organisms/MessageModal';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';

export type LinkedListItem = {
  key: string;
  title: string;
  link: string;
  onClick?: () => void;
  details?: {
    id: number;
    published_at: Date;
    notice?: string;
    file1?: string;
    file2?: string;
    file3?: string;
    file4?: string;
    file5?: string;
  };
};

export const LinkedList: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: LinkedListItem[];
  err: string | null | undefined;
  loading: boolean;
  noItemMsg: string;
}> = (props) => {
  const nav = useNavigate();
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);
  const ctx = useContext(AppContext);
  const [errorModal, setErrorModal] = useState('');
  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tCommons } = useTranslationWithLang('commons');
  // アイテムの展開/折りたたみを切り替える
  const toggleItemExpand = (key: string) => {
    if (expandedItemKey === key) {
      setExpandedItemKey(null); // 同じアイテムをクリックしたら閉じる
    } else {
      setExpandedItemKey(key); // 別のアイテムを開く
    }
  };

  // 通常のリスト表示（元のコード）
  const itemComponents = props.loading ? (
    <li className="flex justify-center text-[--c-text2] pt-2">Loading...</li>
  ) : props.err ? (
    <li className="text-[--c-error] pl-5 text-sm font-bold flex justify-center pt-2">
      {props.err}
    </li>
  ) : props.items.length === 0 ? (
    <li className="text-[--c-text2] pl-5 text-sm font-bold flex justify-center pt-2">
      {props.noItemMsg}
    </li>
  ) : (
    props.items.map((v) => {
      const isExpanded = expandedItemKey === v.key;

      const handleClick = () => {
        if (v.details) {
          toggleItemExpand(v.key);
        } else {
          // 詳細情報がない場合は従来通りの動作
          v.onClick?.();
          nav(v.link);
        }
      };

      return (
        <li key={v.key} className="mx-3 my-2">
          <div
            className={`
              border-[1px] rounded         
              border-[--c-text2] 
              text-[--c-text2] 
              text-center
              hover:text-[--c-background2]
              hover:bg-[--c-text2]
              ${isExpanded ? 'border-b-0 rounded-b-none' : ''}
              break-all
            `}
            onClick={handleClick}
            tabIndex={0}
            title={v.title}
          >
            <button tabIndex={-1}>{v.title}</button>
          </div>

          {isExpanded && v.details && (
            <div className="border-[1px] border-t-0 rounded-b border-[--c-text2] p-3 text-left overflow-hidden w-full break-all">
              {v.details.published_at && (
                <p className="text-[--c-text]">
                  {v.details.published_at
                    .toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })
                    .replace(/\//g, '.')}
                </p>
              )}
              <p className="text-[--c-text2] text-lg">{v.title}</p>
              {v.details.notice && (
                <p className="text-[--c-text2] ml-1 break-words overflow-wrap-anywhere">
                  {v.details.notice
                    .split(/(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi)
                    .map((part, i) =>
                      part.match(/^https?:\/\//i) ? (
                        <a
                          key={i}
                          href={part}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline break-all"
                          aria-label={part}
                        >
                          {part}
                        </a>
                      ) : (
                        part
                      )
                    )}
                </p>
              )}
              {v.details.file1 && (
                <div className="pl-2 text-[--c-text2] truncate max-w-[200px] flex items-center">
                  <AttachFileButton
                    onClick={() =>
                      handleFileDownload(ctx, v.details?.file1, setErrorModal, tMessage)
                    }
                    fileNumber={1}
                  />
                </div>
              )}
              {v.details.file2 && (
                <div className="pl-2 text-[--c-text2] truncate max-w-[200px] flex items-center">
                  <AttachFileButton
                    onClick={() =>
                      handleFileDownload(ctx, v.details?.file2, setErrorModal, tMessage)
                    }
                    fileNumber={2}
                  />
                </div>
              )}
              {v.details.file3 && (
                <div className="pl-2 text-[--c-text2] truncate max-w-[200px] flex items-center">
                  <AttachFileButton
                    onClick={() =>
                      handleFileDownload(ctx, v.details?.file3, setErrorModal, tMessage)
                    }
                    fileNumber={3}
                  />
                </div>
              )}
              {v.details.file4 && (
                <div className="pl-2 text-[--c-text2] truncate max-w-[200px] flex items-center">
                  <AttachFileButton
                    onClick={() =>
                      handleFileDownload(ctx, v.details?.file4, setErrorModal, tMessage)
                    }
                    fileNumber={4}
                  />
                </div>
              )}
              {v.details.file5 && (
                <div className="pl-2 text-[--c-text2] truncate max-w-[200px] flex items-center">
                  <AttachFileButton
                    onClick={() =>
                      handleFileDownload(ctx, v.details?.file5, setErrorModal, tMessage)
                    }
                    fileNumber={5}
                  />
                </div>
              )}
            </div>
          )}
        </li>
      );
    })
  );

  return (
    <>
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tCommons('TU000_028')}
          onClose={() => setErrorModal('')}
        />
      )}
      <div className="rounded-lg max-h-full bg-[--c-background2] pb-2 h-full">
        <div
          className="text-center text-white rounded-t-lg rounded-r-lb font-bold"
          style={{ background: 'var(--c-secondary)' }}
        >
          <div className="flex justify-center items-center ml-[-14px]">
            <div className="flex justify-center items-center relative">{props.icon}</div>
            <h2 className="text-white font-bold pl-[2px]">{props.title}</h2>
          </div>
        </div>
        <div className="h-full px-2 pb-6 ">
          <ul className="h-full overflow-y-auto flex flex-col">{itemComponents}</ul>
        </div>
      </div>
    </>
  );
};
